import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import { FaEye, FaPrint, FaTimes, FaFileAlt, FaCheck } from "react-icons/fa";
import { RiDeleteBin6Line } from "react-icons/ri";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import PageHeading from "../../components/PageHeading";
import parse from "html-react-parser";
import { useNavigate } from "react-router-dom";
import ConfirmActionModal from "../ConfirmDeleteModal";

const API_KEY =
  "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";
const REPORT_API = "https://api.muktihospital.com/api/pathology/reports";

const LabReportList = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedReport, setSelectedReport] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [itemsToDelete, setItemsToDelete] = useState(null);
  const printRef = useRef();
  const navigate = useNavigate();

  const breadcrumbs = [
    { label: "Dashboard", url: "/" },
    { label: "Pathology", url: "/pathology" },
    { label: "All Lab Reports" },
  ];

  // 1. Fetch All Reports
  const fetchReports = async () => {
    try {
      const res = await axios.get(`${REPORT_API}/all`, {
        headers: { "x-api-key": API_KEY },
      });
      setReports(res.data.reports || []);
    } catch (err) {
      toast.error("Failed to load reports");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, []);

  // 2. Filter Logic
  const filteredReports = reports.filter(
    (r) =>
      r.patient?.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.patientId.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.template?.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Pagination Logic
  const totalPages = Math.ceil(filteredReports.length / itemsPerPage);
  const paginatedReports = filteredReports.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = filteredReports.map((item) => item.id);
      setSelectedRows(allIds);
    } else {
      setSelectedRows([]);
    }
  };

  const handleRowSelect = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) setCurrentPage(page);
  };

  const openDeleteModal = (ids) => {
    setItemsToDelete(ids);
    setDeleteModalOpen(true);
  };

 const executeDelete = async (ids) => {
    const isBulk = Array.isArray(ids);
    
    try {
        if (isBulk) {
            await axios.post(`${REPORT_API}/delete-many`, { ids }, { headers: { "x-api-key": API_KEY } });
        } else {
            await axios.delete(`${REPORT_API}/delete/${ids}`, { headers: { "x-api-key": API_KEY } });
        }
        
        toast.success("Deleted successfully");
        
        const idsToDelete = isBulk ? ids : [ids];
        setReports(prev => prev.filter(r => !idsToDelete.includes(r.id)));
        setSelectedRows([]); 
        setItemsToDelete(null);
    } catch (err) {
        toast.error("Failed to delete report(s)");
        console.error(err);
    }
  };

  // 3. PARSER OPTIONS (Clean Data for Preview)
  const getPreviewOptions = (report) => ({
    replace: (domNode) => {
      if (domNode.type === "text" && domNode.data) {
        let text = domNode.data;
        if (text.includes("{{Name}}"))
          text = text.replace(/{{Name}}/g, report.patient?.name || "");
        if (text.includes("{{Age}}"))
          text = text.replace(/{{Age}}/g, report.patient?.age || "");
        if (text.includes("{{Reg}}"))
          text = text.replace(/{{Reg}}/g, report.patientId || "");
        if (text.includes("{{Ref}}"))
          text = text.replace(/{{Ref}}/g, report.referredBy || "");
        if (text.includes("{{Date}}"))
          text = text.replace(
            /{{Date}}/g,
            new Date(report.createdAt).toLocaleDateString("en-GB")
          );

        // Clean __INPUT__ junk if it exists
        if (text.includes("__INPUT")) {
          text = text
            .replace(/__INPUT:(.*?)__/g, "$1")
            .replace(/__INPUT__/g, "");
        }
        return <>{text}</>;
      }

      // Hide Edit Buttons in Preview
      if (
        domNode.name === "button" ||
        (domNode.attribs &&
          (domNode.attribs.class?.includes("add-row-btn") ||
            domNode.attribs.class?.includes("delete-cell")))
      ) {
        return <></>;
      }

      // Fix Inputs to Text
      if (domNode.name === "input") {
        const val = domNode.attribs.value || "";
        return <span className="font-medium text-gray-900">{val}</span>;
      }
    },
  });

  const handlePrint = () => {
    window.print();
  };

  return (
    <div>
      <ToastContainer position="top-right" className="print:hidden" />
      <div className="print:hidden">
        <PageHeading title="Lab Reports" breadcrumbs={breadcrumbs} />
      </div>

      <div className="bg-white shadow-sm rounded-lg print:hidden">
        <div className="flex flex-wrap gap-3 flex-col sm:flex-row md:items-center justify-between border-b border-dashed border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
              All Generated Reports
            </h2>
            {selectedRows.length > 0 && (
              <button
                onClick={() => openDeleteModal(selectedRows)}
                className="size-7 bg-M-secondary-color/10 text-M-secondary-color hover:bg-M-secondary-color hover:text-white transition-all duration-300 text-base rounded-md inline-flex items-center justify-center"
                title="Delete selected rows"
              >
                <RiDeleteBin6Line size={16} />
              </button>
            )}
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-start sm:justify-end gap-3">
            <input
              type="text"
              className="max-w-48 w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-gray-700 shrink-0"
              placeholder="Search Report..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button
              onClick={() => navigate("/pathology-lab-test")}
              className="bg-[#73CB64] hover:bg-green-600 text-white px-4 py-2 rounded-md text-sm font-medium shrink-0 transition-colors"
            >
              + Add Lab Test
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="min-w-[1000px] w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left print-hidden-in-print">
                  <div className="relative left-3 -top-[2px]">
                    <input
                        type="checkbox"
                        id="selectAllReports" 
                        className="hidden peer print-hidden"
                        onChange={handleSelectAll}
                        checked={
                          filteredReports.length > 0 &&
                          filteredReports.every(
                            (item) =>
                              item && item.id && selectedRows.includes(item.id)
                          )
                        }
                      />
                    <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0 print-hidden">
                      <FaCheck className="text-slate-100" />
                    </span>
                    <label
                      htmlFor="selectAllReports"
                      className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6 print-hidden"
                    ></label>
                  </div>
                </th>
                <th className="text-left uppercase font-inter font-medium text-sm text-gray-500 p-3">
                  No.
                </th>
                <th className="text-left uppercase font-inter font-medium text-sm text-gray-500 p-3">
                  Date
                </th>
                <th className="text-left uppercase font-inter font-medium text-sm text-gray-500 p-3">
                  Patient Name
                </th>
                <th className="text-left uppercase font-inter font-medium text-sm text-gray-500 p-3">
                  Reg No
                </th>
                <th className="text-left uppercase font-inter font-medium text-sm text-gray-500 p-3">
                  Test Name
                </th>
                <th className="text-left uppercase font-inter font-medium text-sm text-gray-500 p-3">
                  Referred By
                </th>
                <th className="text-left uppercase font-inter font-medium text-sm text-gray-500 p-3">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan="7" className="text-center py-8 text-gray-500">
                    Loading...
                  </td>
                </tr>
              ) : paginatedReports.length > 0 ? (
                paginatedReports.map((report, i) => (
                  <tr
                    key={report.id}
                    className={`hover:bg-gray-50 transition-colors border-b ${
                      selectedRows.includes(report.id) ? "bg-blue-50/50" : ""
                    }`}
                  >
                    <td className="text-left capitalize font-inter font-medium text-sm text-M-heading-color print-hidden-in-print">
                      <div className="relative left-3 -top-[3px]">
                        <input
                          type="checkbox"
                          id={`test-${i}`}
                          className="hidden peer print-hidden"
                          checked={selectedRows.includes(report.id)}
                          onChange={() => handleRowSelect(report.id)}
                        />
                        <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0 print-hidden">
                          <FaCheck className="text-slate-100" />
                        </span>
                        <label
                          htmlFor={`test-${i}`}
                          className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6 print-hidden"
                        ></label>
                      </div>
                    </td>
                    <td className="p-3 text-sm text-gray-700 font-inter">
                      {(currentPage - 1) * itemsPerPage + i + 1}
                    </td>
                    <td className="p-3 text-sm text-gray-500 font-inter">
                      {new Date(report.createdAt).toLocaleDateString()}
                    </td>
                    <td className="p-3 text-sm font-medium text-gray-800 font-inter capitalize">
                      {report.patient?.name}
                    </td>
                    <td className="p-3 text-sm text-gray-500 font-mono">
                      {report.patientId}
                    </td>
                    <td className="p-3 text-sm text-blue-600 font-medium font-inter">
                      {report.template?.name}
                    </td>
                    <td className="p-3 text-sm text-gray-500 font-inter">
                      {report.referredBy || "Self"}
                    </td>
                    <td className="p-3 flex gap-2">
                      <button
                        onClick={() => setSelectedReport(report)}
                        className="size-7 rounded-full bg-M-primary-color/10 text-M-primary-color text-sm inline-flex items-center justify-center hover:bg-M-primary-color hover:text-white transition-all duration-300"
                        title="View Report"
                      >
                        <FaEye size={14} />
                      </button>
                      <button
                        onClick={() => openDeleteModal(report.id)}
                        className="size-7 rounded-full bg-M-secondary-color/10 text-M-secondary-color text-sm inline-flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300"
                        title="Delete Report"
                      >
                        <RiDeleteBin6Line size={14} />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="7"
                    className="text-center py-8 text-gray-400 font-inter text-sm"
                  >
                    No reports found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="p-4 flex items-center justify-between border-t border-gray-200">
            <p className="text-sm text-gray-600">
              Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
              {Math.min(currentPage * itemsPerPage, filteredReports.length)} of{" "}
              {filteredReports.length} entries
            </p>
            <div className="flex items-center gap-1">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 transition-colors"
              >
                <MdKeyboardArrowLeft size={20} />
              </button>
              {[...Array(totalPages)].map((_, i) => (
                <button
                  key={i}
                  onClick={() => handlePageChange(i + 1)}
                  className={`w-7 h-7 rounded text-xs font-medium transition-colors ${
                    currentPage === i + 1
                      ? "bg-blue-600 text-white"
                      : "hover:bg-gray-100 text-gray-600"
                  }`}
                >
                  {i + 1}
                </button>
              ))}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 transition-colors"
              >
                <MdKeyboardArrowRight size={20} />
              </button>
            </div>
          </div>
        )}
      </div>

      <ConfirmActionModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={executeDelete}
        ids={itemsToDelete}
        entityName="Lab Report"
        title="Delete Report?"
        confirmText="Delete"
        cancelText="Cancel"
      />

      {/* --- PREVIEW MODAL (Keep Existing Logic) --- */}
      {selectedReport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 print:p-0 print:bg-white print:block">
          <div className="bg-white w-full max-w-4xl h-[90vh] rounded-xl shadow-2xl flex flex-col overflow-hidden print:shadow-none print:h-auto print:w-full print:max-w-none print:rounded-none">
            <div className="flex justify-between items-center px-6 py-4 border-b bg-gray-50 print:hidden">
              <h3 className="font-bold text-gray-700 text-lg flex items-center gap-2">
                <FaFileAlt className="text-blue-600" /> Report Preview
              </h3>
              <div className="flex gap-3">
                <button
                  onClick={handlePrint}
                  className="flex items-center gap-2 bg-[#60b452] hover:bg-[#4a963e] text-white px-4 py-2 rounded-lg text-sm font-bold shadow-md transition-all"
                >
                  <FaPrint /> Print
                </button>
                <button
                  onClick={() => setSelectedReport(null)}
                  className="text-gray-400 hover:text-red-500 transition-colors p-1"
                >
                  <FaTimes size={20} />
                </button>
              </div>
            </div>
            <div className="overflow-y-auto bg-gray-100 p-8 print:p-0 print:bg-white print:overflow-visible print:block">
              <div
                id="print-area"
                className="shadow-lg print:shadow-none print:w-full"
              >
                <div
                  ref={printRef}
                  className="bg-white relative flex flex-col justify-between print:w-full print:min-h-0 print:mx-0 print:h-auto"
                >
                  <div className="bg-white flex flex-col justify-between min-h-[297mm] relative">
                    {parse(
                      selectedReport.reportContent,
                      getPreviewOptions(selectedReport)
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* GLOBAL STYLES FOR PREVIEW & PRINT */}
      <style>{`
        .header-gradient { background: linear-gradient(120deg, #3f54e7, #60b452) !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        .footer-shape { content: ''; position: absolute; top: 0; right: 0; width: 150px; height: 100%; background-color: #60b452 !important; clip-path: polygon(25% 0%, 100% 0%, 100% 100%, 0% 100%); -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        .print-bg-force { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }

        /* 🔥 FIX FOR PRINTING */
        @media print { 
            @page { margin: 0; size: A4; }
            body * { visibility: hidden; }
            #print-area, #print-area * { visibility: visible; }
            #print-area { position: absolute; left: 0; top: 0; width: 100%; margin: 0; padding: 0; background: white; }
            input::placeholder { color: transparent; }
            input { border-bottom: none !important; }
        }
      `}</style>
    </div>
  );
};

export default LabReportList;
